import { derived, writable, get } from "svelte/store";
import { getMediaQuery } from "app/utils/mediaQuery";
import { v4 as uuidv4 } from "uuid";
import type { Writable } from "svelte/store";
import type { ConversationItem } from "app/components/widget/models";
import type { UserSession } from "@outverse-video/outverse-api-client-lib";

interface Conversation {
  id: string;
  traces: ConversationItem[];
  processing: boolean;
  preventGatheringResponse: boolean;
  lastResponse: string;
  hideEscalationButton: boolean;
  escalated: boolean;
  escalationDrain?: string | null;
}

// CONVERSATION STORES

// Store for the user sessions list
export const userSessions: Writable<UserSession[]> = writable([]);

// Store all conversations, keyed by ID
export const conversations: Writable<Record<string, Conversation>> = writable({});

// Store the ID of the currently active conversation
export const conversationId: Writable<string | null> = writable(null);

// Derived store for the currently active conversation object
export const conversation = derived(
  [conversations, conversationId],
  ([$conversations, $conversationId]) => {
    return $conversationId ? $conversations[$conversationId] : null;
  }
);

// Derived store for the processing state of the current conversation
export const processing = derived(conversation, ($conversation) => !!$conversation?.processing);

// Creates a new empty conversation, adds it to the store, and sets it as the current one.
export const createNewConversation = () => {
  const id = uuidv4();
  const newConversation: Conversation = {
    id,
    traces: [],
    processing: false,
    preventGatheringResponse: false,
    lastResponse: "",
    hideEscalationButton: false,
    escalated: false,
  };
  conversations.update(($conversations) => {
    $conversations[id] = newConversation;
    return $conversations;
  });
  conversationId.set(id);
  return id; // return the new id
};

// Sets the current conversation by ID
export const setconversationId = (id: string) => {
  const existingConversations = get(conversations);
  if (existingConversations[id]) {
    conversationId.set(id);
  }
};

// Updates a specific conversation by ID
export const updateConversation = (id: string | undefined, updates: Partial<Conversation>) => {
  if (!id) return;
  console.log("Updating conversation", id, updates);
  conversations.update(($conversations) => {
    // Ensure the conversation exists before spreading it
    const existingConv = $conversations[id] || { id: id, processing: false, traces: [] };
    $conversations[id] = { ...existingConv, ...updates };
    return $conversations;
  });
};

// WIDGET STORES

// Store for the widget placeholder
export const DEFAULT_WIDGET_PLACEHOLDER = "Ask a question...";
export const placeholder = writable(DEFAULT_WIDGET_PLACEHOLDER);
export const DEFAULT_WIDGET_HOMEHEADING = "How can I help?";
export const homeHeading = writable(DEFAULT_WIDGET_HOMEHEADING);
export const DEFAULT_WIDGET_HOMESUBHEADING = "AI responds immediately";
export const homeSubHeading = writable(DEFAULT_WIDGET_HOMESUBHEADING);

// Store for the widget expanded state
export const isWidgetExpanded = writable(false);
export const isWidgetOpen = writable(false);

// Store for the iframe parent tablet or desktop state
export const iframeParentIsTabletOrDesktop = writable(false);

// Is iframe parent tablet or desktop, or is the current window tablet or desktop
export const isTabletOrDesktop = derived(
  [iframeParentIsTabletOrDesktop, getMediaQuery("tablet")],
  ([$iframeParentMatches, $windowMatches]) => $iframeParentMatches || $windowMatches
);
